import React, { useEffect, useState } from "react";
import { Moon, Play, Stop } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAudio } from "../context/AudioContext";

/** Home-page nightly prayer card. Mirrors the Verse-of-the-Day pattern but
 *  for The Lord's Prayer + a rotating one-line intention. Tap "Pray with me"
 *  to hear it read aloud — the text stays fully visible (like every other
 *  read-aloud) and the line being spoken glows so you can follow along. */
export default function TonightPrayer() {
  const [data, setData] = useState(null);
  const audio = useAudio();

  useEffect(() => {
    api.get("/tonight-prayer").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  // Is the audio session currently ours (the prayer queue)?
  const isOurs = audio?.queue?.[0]?.id === "lp-0";
  const playingPrayer = isOurs && audio?.isPlaying;
  const activeIdx = playingPrayer ? audio.currentIndex : -1;

  const playPrayer = () => {
    if (!audio) return;
    if (playingPrayer) {
      audio.stop?.();
      return;
    }
    const queue = data.lines.map((line, i) => ({
      id: `lp-${i}`,
      text: line,
      label: `Line ${i + 1}`,
    }));
    audio.play(queue, { title: data.title, reference: data.reference }, "en", "en-US");
  };

  return (
    <section
      data-testid="tonight-prayer"
      className="mt-16 md:mt-24 relative rounded-[2rem] overflow-hidden border border-forest/20"
      style={{ background: "radial-gradient(ellipse at top right, #1a3a2a 0%, #0d2a1d 70%)" }}
    >
      {/* Subtle starry overlay */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.6) 1px, transparent 1.5px), radial-gradient(circle at 70% 60%, rgba(255,255,255,0.5) 1px, transparent 1.5px), radial-gradient(circle at 40% 80%, rgba(255,255,255,0.4) 1px, transparent 1.5px), radial-gradient(circle at 85% 15%, rgba(255,255,255,0.6) 1px, transparent 1.5px)",
          backgroundSize: "120px 120px, 180px 180px, 200px 200px, 90px 90px",
        }}
      />
      <div className="relative px-7 md:px-14 py-12 md:py-16 max-w-3xl">
        <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-terracotta font-semibold">
          <Moon size={14} weight="fill" /> Tonight's Prayer · {data.date}
        </span>
        <h2 className="font-heading text-4xl md:text-5xl text-cream mt-4 italic leading-[1.1]">{data.title}</h2>
        <p className="text-cream/60 text-xs md:text-sm tracking-wider mt-1 font-medium">{data.reference}</p>

        <p className="text-cream/85 mt-6 text-sm md:text-base italic font-light max-w-xl" data-testid="tonight-prayer-intention">
          {data.intention}
        </p>

        <div className="mt-7 space-y-1.5 text-cream text-base md:text-lg leading-relaxed font-heading">
          {data.lines.map((line, i) => (
            <p
              key={i}
              data-testid={`tonight-prayer-line-${i}`}
              className={`transition-all duration-300 ${
                activeIdx === i
                  ? "text-terracotta font-medium translate-x-2"
                  : playingPrayer
                  ? "opacity-50"
                  : i === data.lines.length - 1
                  ? "font-medium pt-2"
                  : "opacity-90"
              }`}
            >
              {line}
            </p>
          ))}
        </div>

        <button
          onClick={playPrayer}
          data-testid="tonight-prayer-play"
          className="mt-8 inline-flex items-center gap-2 px-6 h-11 text-terracotta hover:text-terracotta/80 hover:bg-terracotta-600 transition-all hover:scale-105 text-sm font-medium"
        >
          {playingPrayer ? (
            <><Stop size={14} weight="fill" /> Stop</>
          ) : (
            <><Play size={14} weight="fill" /> Pray with me</>
          )}
        </button>
        <p className="text-cream/50 text-[11px] mt-3 italic">Supporters also get this in their inbox at 9 pm.</p>
      </div>
    </section>
  );
}
